import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyGetServiceAreasQuery } from '../api';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { ServiceArea } from '../serviceAreas.model';
export interface ServiceAreasQueryParams {
  page?: number;
  limit?: number | null;
  searchKey?: string;
  searchValue?: string;
  status?: string;
  colorCode?: string;
  members?: string;
}

export const emptyQueryParamsState: ServiceAreasQueryParams = {
  searchKey: 'name',
  searchValue: null,
  status: null,
  colorCode: null,
  members: null,
  page: 0,
  limit: 50
};

export const usePaginatedGetServiceAreasQuery = (
  defaultLimit: number | null = 50,
  initialQueryParams: Partial<ServiceAreasQueryParams> = {}
) => {
  const [list, setList] = useState<ServiceArea[]>([]);
  const [queryParams, setQueryParams] = useState<ServiceAreasQueryParams>({
    ...emptyQueryParamsState,
    limit: defaultLimit,
    ...initialQueryParams
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoadingSuccess, setIsLoadingSuccess] = useState<boolean>(false);
  const page = useRef(0);
  const [getServiceAreas, serviceAreasState] = useLazyGetServiceAreasQuery();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { viewJobSettings } = useAppSelector(selectCurrentTeamJobPermissions);

  useEffect(() => {
    if (!serviceAreasState?.isFetching && serviceAreasState.isSuccess) {
      const response = serviceAreasState?.data;
      if (response) {
        setList(response.data || []);
        setTotalCount(response.totalCount || 0);
        setIsLoading(false);
        setIsLoadingSuccess(true);
        setIsError(false);
      }
    }
  }, [serviceAreasState?.isFetching]);

  useEffect(() => {
    if (!serviceAreasState?.isFetching && serviceAreasState?.isError) {
      setIsLoading(false);
      setIsError(true);
      setIsLoadingSuccess(false);
    }
  }, [serviceAreasState?.isError]);

  const fetchData = useCallback(
    (pageNumber: number) => {
      setIsLoading(true);
      if (currentTeam?.id && viewJobSettings) {
        page.current = pageNumber;
        try {
          getServiceAreas({
            teamId: currentTeam.id.toString(),
            params: {
              ...queryParams,
              page: pageNumber,
              limit: queryParams.limit ? queryParams.limit : null,
              ...(queryParams.searchValue ? { searchKey: 'name' } : { searchKey: null })
            }
          });
        } catch (err) {
          setIsError(true);
          setIsLoadingSuccess(false);
        }
      }
    },
    [currentTeam?.id, queryParams, viewJobSettings]
  );

  const resetQuery = () => {
    setQueryParams({ ...emptyQueryParamsState, limit: defaultLimit });
    page.current = 0;
    fetchData(0);
  };

  useEffect(() => {
    fetchData(page.current);
  }, [queryParams, currentTeam?.id]);

  return {
    isLoading,
    isFetching: serviceAreasState?.isFetching,
    list,
    isError,
    fetchData,
    resetQuery,
    setQueryParams,
    queryParams,
    page: page.current,
    totalCount,
    limit: queryParams.limit,
    isLoadingSuccess
  };
};
